import React, { useCallback, useMemo } from "react";
import { View, StyleSheet, StyleProp, ViewStyle, ColorValue } from "react-native";
import ListItem, { ListItemProps } from "../ui_atoms/ListItem";
import { useTheme } from "../hooks/use-theme";

type ListGroupAlignment = "leading" | "center" | "trailing" | "stretch";

export type ListGroupItem<T> = {
    value: T;
} & ListItemProps;

export type ListGroupProps<T> = {
    alignment?: ListGroupAlignment;
    style?: StyleProp<ViewStyle>;
    itemStyle?: ViewStyle;
    value?: T | null;
    onPressItem?: (val: T) => void;
    items: ListGroupItem<T>[];
    testID?: string;
    borderColor?: ColorValue;
    isItemsCheckable?: boolean;
};

const ALIGNMENT_MAP: Record<ListGroupAlignment, StyleProp<ViewStyle>> = {
    leading: { alignItems: "flex-start" },
    center: { alignItems: "center" },
    trailing: { alignItems: "flex-end" },
    stretch: { alignItems: "stretch" },
};

const styles = StyleSheet.create({
    group: {
        flexDirection: "column",
        width: "100%",
    },
});

export type ListGroupRowProps<T> = {
    item: ListGroupItem<T>;
    isFirst: boolean;
    isLast: boolean;
    itemStyle?: ViewStyle;
    borderColor?: ColorValue;
    value?: T | null;
    onPressItem?: (val: T) => void;
    isItemsCheckable?: boolean;
};

/**
 * A single grouped row, extracted from ListGroup so it can be reused both inside
 * the (non-virtualized) ListGroup container and as a `renderItem` for a
 * screen-level virtualized list (see ScreenCard `virtualizedItems`/`renderItem`).
 */
export const ListGroupRow = <T extends string>({
    item: { style: _itemStyle, ...item },
    isFirst,
    isLast,
    itemStyle,
    borderColor,
    value,
    onPressItem,
    isItemsCheckable = true,
}: ListGroupRowProps<T>) => {
    const listGroupStyle = useListGroupStyle();

    return (
        <ListItem
            style={[
                {
                    borderRadius: 0,
                    width: "100%",
                },
                itemStyle,
                _itemStyle,
                ...(isFirst ? [listGroupStyle.firstItem] : []),
                ...(!isFirst ? [listGroupStyle.middleItem] : []),
                ...(isLast ? [listGroupStyle.lastItem] : []),
                { borderColor },
            ]}
            testID={item.testID}
            onPress={item.onPress || onPressItem ? () => onPressItem?.(item.value) : undefined}
            checked={isItemsCheckable && value != undefined ? value === item.value : undefined}
            {...item}
        />
    );
};

export type ListGroupRenderItemOptions<T> = {
    itemStyle?: ViewStyle;
    borderColor?: ColorValue;
    value?: T | null;
    onPressItem?: (val: T) => void;
    isItemsCheckable?: boolean;
};

/**
 * Builds a `renderItem` callback that renders a `ListGroupRow`, for use with ScreenCard's
 * `virtualizedItems`/`renderItem`. Keeps the first/middle/last grouped styling that ListGroup
 * applies, so a virtualized list looks identical to the non-virtualized group.
 */
export const useListGroupRenderItem = <T extends string>(
    items: ReadonlyArray<ListGroupItem<T>>,
    {
        itemStyle,
        borderColor,
        value,
        onPressItem,
        isItemsCheckable,
    }: ListGroupRenderItemOptions<T> = {},
) => {
    const total = items.length;
    return useCallback(
        (item: ListGroupItem<T>, index: number) => (
            <ListGroupRow
                item={item}
                isFirst={index === 0}
                isLast={index === total - 1}
                itemStyle={itemStyle}
                borderColor={borderColor}
                value={value}
                onPressItem={onPressItem}
                isItemsCheckable={isItemsCheckable}
            />
        ),
        [total, itemStyle, borderColor, value, onPressItem, isItemsCheckable],
    );
};

const ListGroup = <T extends string>({
    alignment = "leading",
    style,
    itemStyle,
    items,
    value,
    onPressItem,
    isItemsCheckable = true,
    borderColor,
    testID,
}: ListGroupProps<T>) => {
    return (
        <View testID={testID} style={[styles.group, ALIGNMENT_MAP[alignment], style]}>
            {items.map((item, idx) => (
                <ListGroupRow
                    key={`${item.title}-${item.value}`}
                    item={item}
                    isFirst={idx === 0}
                    isLast={idx === items.length - 1}
                    itemStyle={itemStyle}
                    borderColor={borderColor}
                    value={value}
                    onPressItem={onPressItem}
                    isItemsCheckable={isItemsCheckable}
                />
            ))}
        </View>
    );
};

const useListGroupStyle = () => {
    const { colors, fonts } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                headerText: {
                    ...fonts.headlineSmall,
                },
                bodyText: {
                    ...fonts.bodyMedium,
                },
                firstItem: {},
                middleItem: {
                    borderTopColor: colors.accentLighter,
                },
                lastItem: {
                    borderBottomColor: colors.accentLighter,
                },
            }),
        [fonts.headlineSmall, fonts.bodyMedium, colors.accentLighter],
    );
};

export default ListGroup;
